/**
 * Admin Billing Section - Test settings for a producer's billing
 * 
 * Allows admins to:
 * - Toggle global paid plans (kill-switch)
 * - Set plan directly (free/starter/pro)
 * 
 * NOTE: Credits and feature_overrides exist in DB but are dormant (not shown in UI).
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Settings, 
  CreditCard, 
  Loader2,
  Power,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  useProducerBillingState,
  useAdminSetProducerPlan,
  useAdminSetPaidPlansEnabled,
  PLAN_LABELS,
} from '@/hooks/useProducerBilling';

interface AdminBillingSectionProps {
  producerId: string;
}

export function AdminBillingSection({ producerId }: AdminBillingSectionProps) {
  const { data: billing, isLoading, refetch } = useProducerBillingState(producerId);
  
  const setProducerPlan = useAdminSetProducerPlan();
  const setPaidPlansEnabled = useAdminSetPaidPlansEnabled();
  
  if (isLoading || !billing) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Testinställningar
          </CardTitle>
        </CardHeader>
        <CardContent className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }
  
  const handleSetPlan = async (plan: 'free' | 'starter' | 'pro') => {
    try {
      await setProducerPlan.mutateAsync({
        producerId,
        planId: plan,
      });
      toast.success(`Plan satt till ${PLAN_LABELS[plan]?.name}`);
      refetch();
    } catch (error: any) {
      toast.error(error.message || 'Kunde inte sätta plan');
    }
  };
  
  const handleTogglePaidPlans = async () => {
    const newState = !billing.paid_plans_enabled;
    try {
      await setPaidPlansEnabled.mutateAsync(newState);
      toast.success(newState 
        ? 'Betalplaner aktiverade – Starter och Pro är nu tillgängliga' 
        : 'Betalplaner avstängda – Alla värdar behandlas som Gratis'
      );
      refetch();
    } catch (error: any) {
      toast.error(error.message || 'Kunde inte ändra betalplaner');
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Testinställningar (Admin)
        </CardTitle>
        <CardDescription>
          Admin-verktyg för att testa planer
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current State Overview */}
        <div className="flex flex-wrap gap-3">
          <Badge variant="outline" className="gap-1">
            <CreditCard className="h-3 w-3" />
            Effektiv plan: {PLAN_LABELS[billing.effective_plan]?.name}
          </Badge>
          <Badge 
            variant={billing.paid_plans_enabled ? 'default' : 'destructive'} 
            className="gap-1"
          >
            <Power className="h-3 w-3" />
            Betalplaner: {billing.paid_plans_enabled ? 'PÅ' : 'AV'}
          </Badge>
        </div>
        
        <Separator />
        
        {/* Global Paid Plans Toggle */}
        <div className="space-y-3">
          <Label className="flex items-center gap-2">
            <Power className="h-4 w-4" />
            Global kill-switch: Betalplaner
          </Label>
          <div className="flex items-center justify-between rounded-lg border p-4 bg-muted/30">
            <div>
              <p className="font-medium">
                {billing.paid_plans_enabled 
                  ? 'Betalplaner är AKTIVA' 
                  : 'Betalplaner är AVSTÄNGDA'}
              </p>
              <p className="text-sm text-muted-foreground">
                {billing.paid_plans_enabled 
                  ? 'Starter och Pro-planer fungerar normalt' 
                  : 'Alla värdar behandlas som Gratis'}
              </p>
            </div>
            <Switch
              checked={billing.paid_plans_enabled}
              onCheckedChange={handleTogglePaidPlans}
              disabled={setPaidPlansEnabled.isPending}
            />
          </div>
          {!billing.paid_plans_enabled && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Kill-switch aktiv!</strong> Alla producenter behandlas som Gratis, 
                oavsett deras prenumeration.
              </AlertDescription>
            </Alert>
          )}
        </div>
        
        <Separator />
        
        {/* Plan Selector */}
        <div className="space-y-3">
          <Label className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Sätt plan för denna producent
          </Label>
          <div className="flex gap-2">
            <Select
              value={billing.effective_plan}
              onValueChange={(value) => handleSetPlan(value as 'free' | 'starter' | 'pro')}
              disabled={setProducerPlan.isPending}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Välj plan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="free">Gratis</SelectItem>
                <SelectItem value="starter">Starter</SelectItem>
                <SelectItem value="pro">Pro</SelectItem>
              </SelectContent>
            </Select>
            {setProducerPlan.isPending && (
              <Loader2 className="h-4 w-4 animate-spin self-center" />
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Sätter producentens abonnemangsplan. Fungerar bara när betalplaner är PÅ.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
